import React, { useState } from "react";
import { Box, Typography, keyframes, Button } from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CancelIcon from "@mui/icons-material/Cancel";
import PersonIcon from "@mui/icons-material/Person";
import ScienceIcon from "@mui/icons-material/Science";
import BloodtypeIcon from "@mui/icons-material/Bloodtype";
import LocalPharmacyIcon from "@mui/icons-material/LocalPharmacy";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import RecommendIcon from "@mui/icons-material/Recommend";
// Import icon images
import HemoglobinIcon from "../assets/icons/280e1057-61b9-432a-abf2-35b9025a7350.png";
import WBCIcon from "../assets/icons/e535b285-f349-4042-8c68-44452923cbef.png";
import GlucoseIcon from "../assets/icons/6192150.png";

// ============ ANIMATIONS ============
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
`;

// ============ COMPACT RANGE BAR ============
const CompactRangeBar = ({ value, min, max, normalMin, normalMax, unit }) => {
  const totalRange = max - min;
  const normalStartPercent = ((normalMin - min) / totalRange) * 100;
  const normalWidthPercent = ((normalMax - normalMin) / totalRange) * 100;
  const valuePercent = Math.min(Math.max(((value - min) / totalRange) * 100, 2), 98);

  const isNormal = value >= normalMin && value <= normalMax;
  const markerColor = isNormal ? "#10b981" : "#ef4444";

  return (
    <Box sx={{ width: "100%", marginTop: "0px" }}>
      {/* Range Bar Container */}
      <Box sx={{ position: "relative", height: "38px", display: "flex", alignItems: "flex-end", marginBottom: "2px" }}>
        {/* The Bar */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: "10px",
            borderRadius: "5px",
            overflow: "visible",
          }}
        >
          {/* Low Range Zone (Medium Dark Red) */}
          <Box
            sx={{
              position: "absolute",
              left: "0%",
              width: `${normalStartPercent}%`,
              height: "100%",
              background: "linear-gradient(90deg, #ef4444, #dc2626)",
              borderTopLeftRadius: "5px",
              borderBottomLeftRadius: "5px",
            }}
          />

          {/* Normal Range Zone (Green) */}
          <Box
            sx={{
              position: "absolute",
              left: `${normalStartPercent}%`,
              width: `${normalWidthPercent}%`,
              height: "100%",
              background: "linear-gradient(90deg, #86efac, #4ade80)",
              boxShadow: "0 2px 8px rgba(16, 185, 129, 0.3)",
            }}
          />

          {/* High Range Zone (Medium Dark Red) */}
          <Box
            sx={{
              position: "absolute",
              left: `${normalStartPercent + normalWidthPercent}%`,
              width: `${100 - normalStartPercent - normalWidthPercent}%`,
              height: "100%",
              background: "linear-gradient(90deg, #dc2626, #ef4444)",
              borderTopRightRadius: "5px",
              borderBottomRightRadius: "5px",
            }}
          />

          {/* Low Label - Left Side */}
          <Box
            sx={{
              position: "absolute",
              left: "0%",
              top: "-20px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                background: "rgba(254, 226, 226, 0.85)",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
                border: "1px solid #dc2626",
                color: "#dc2626",
                padding: "2px 6px",
                borderRadius: "4px",
                fontSize: "8px",
                fontFamily: "'Inter', sans-serif",
                fontWeight: 600,
                whiteSpace: "nowrap",
                boxShadow: "0 2px 8px rgba(220, 38, 38, 0.2)",
                letterSpacing: "0.3px",
              }}
            >
              Low
            </Box>
          </Box>

          {/* High Label - Right Side */}
          <Box
            sx={{
              position: "absolute",
              right: "0%",
              top: "-20px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                background: "rgba(254, 226, 226, 0.85)",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
                border: "1px solid #dc2626",
                color: "#dc2626",
                padding: "2px 6px",
                borderRadius: "4px",
                fontSize: "8px",
                fontFamily: "'Inter', sans-serif",
                fontWeight: 600,
                whiteSpace: "nowrap",
                boxShadow: "0 2px 8px rgba(220, 38, 38, 0.2)",
                letterSpacing: "0.3px",
              }}
            >
              High
            </Box>
          </Box>

          {/* Value Marker */}
          <Box
            sx={{
              position: "absolute",
              left: `${valuePercent}%`,
              bottom: "100%",
              transform: "translateX(-50%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              marginBottom: "4px",
            }}
          >
            <Box
              sx={{
                background: isNormal
                  ? "rgba(220, 252, 231, 0.9)"
                  : "rgba(254, 226, 226, 0.9)",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
                border: isNormal
                  ? "1px solid #059669"
                  : "1px solid #dc2626",
                color: isNormal
                  ? "#059669"
                  : "#dc2626",
                padding: "3px 8px",
                borderRadius: "5px",
                fontSize: "11px",
                fontFamily: "'Inter', sans-serif",
                fontWeight: 700,
                whiteSpace: "nowrap",
                boxShadow: isNormal
                  ? "0 4px 12px rgba(5, 150, 105, 0.25)"
                  : "0 4px 12px rgba(220, 38, 38, 0.25)",
                letterSpacing: "0.2px",
              }}
            >
              {value}{unit}
            </Box>
            <Box
              sx={{
                width: 0,
                height: 0,
                borderLeft: "6px solid transparent",
                borderRight: "6px solid transparent",
                borderTop: isNormal
                  ? "8px solid #059669"
                  : "8px solid #dc2626",
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Labels Row */}
      <Box sx={{ display: "flex", justifyContent: "space-between", marginTop: "4px" }}>
        <Typography sx={{ fontSize: "9px", color: "#9ca3af", fontFamily: "'Inter', sans-serif" }}>{min}</Typography>
        <Typography sx={{ fontSize: "9px", color: "#10b981", fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>
          Normal: {normalMin}-{normalMax}
        </Typography>
        <Typography sx={{ fontSize: "9px", color: "#9ca3af", fontFamily: "'Inter', sans-serif" }}>{max}</Typography>
      </Box>
    </Box>
  );
};

// ============ TRADITIONAL REPORT CARD ============
const TraditionalReportCard = ({ testData, sx }) => (
  <Box
    sx={{
      width: "100%",
      background: "linear-gradient(145deg, #f8fafc 0%, #f1f5f9 100%)",
      borderRadius: "24px",
      padding: { xs: "16px", sm: "20px" },
      border: "2px solid #e2e8f0",
      position: "relative",
      overflow: "hidden",
      animation: `${slideInLeft} 0.8s ease-out`,
      minHeight: { xs: "285px", sm: "315px" },
      display: "flex",
      flexDirection: "column",
      ...sx,
    }}
  >
    {/* Faded Badge */}
    <Box
      sx={{
        position: "absolute",
        top: "16px",
        right: "16px",
        background: "#e2e8f0",
        borderRadius: "20px",
        padding: "4px 12px",
      }}
    >
      <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "10px", fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px" }}>
        Old Way
      </Typography>
    </Box>

    {/* Header */}
    <Box sx={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px", marginTop: "4px" }}>
      <Box
        sx={{
          width: "40px",
          height: "40px",
          borderRadius: "12px",
          background: "linear-gradient(145deg, #e2e8f0, #cbd5e1)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "inset 0 2px 4px rgba(0,0,0,0.06)",
        }}
      >
        <DescriptionIcon sx={{ fontSize: "20px", color: "#64748b" }} />
      </Box>
      <Box>
        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: { xs: "16px", sm: "18px" }, fontWeight: 700, color: "#475569" }}>
          Traditional Report
        </Typography>
        <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "11px", color: "#94a3b8" }}>
          Just numbers, no context
        </Typography>
      </Box>
    </Box>

    {/* Patient Details - Top */}
    <Box sx={{ marginBottom: "12px", padding: "10px 12px", background: "#fff", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px" }}>
        <PersonIcon sx={{ fontSize: "14px", color: "#64748b" }} />
        <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "10px", color: "#64748b", fontWeight: 600 }}>
          Patient Details
        </Typography>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "9px", color: "#94a3b8" }}>Name:</Typography>
          <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "9px", color: "#475569", fontWeight: 500, marginTop: "2px" }}>John Doe</Typography>
        </Box>
        <Box sx={{ textAlign: "right" }}>
          <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "9px", color: "#94a3b8" }}>Age:</Typography>
          <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "9px", color: "#475569", fontWeight: 500, marginTop: "2px" }}>35 Years</Typography>
        </Box>
      </Box>
    </Box>

    {/* Lab Details */}
    <Box sx={{ marginBottom: "12px", padding: "10px 12px", background: "#fff", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px" }}>
        <ScienceIcon sx={{ fontSize: "14px", color: "#64748b" }} />
        <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "10px", color: "#64748b", fontWeight: 600 }}>
          Lab Details
        </Typography>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "9px", color: "#94a3b8" }}>Lab:</Typography>
          <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "9px", color: "#475569", fontWeight: 500 }}>ABC Diagnostics</Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "9px", color: "#94a3b8" }}>Report ID:</Typography>
          <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "9px", color: "#475569", fontWeight: 500 }}>RPT-2024-001</Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "9px", color: "#94a3b8" }}>Date:</Typography>
          <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "9px", color: "#475569", fontWeight: 500 }}>15 Jan 2024</Typography>
        </Box>
      </Box>
    </Box>

    {/* Data Items */}
    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", flex: 1 }}>
      {testData.map((item, idx) => (
        <Box
          key={idx}
          sx={{
            padding: "12px 14px",
            background: "#fff",
            borderRadius: "10px",
            border: "1px dashed #d1d5db",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            transition: "all 0.3s ease",
          }}
        >
          <Box>
            <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: { xs: "12px", sm: "13px" }, color: "#374151", fontWeight: 500 }}>
              {item.name}
            </Typography>
            <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "10px", color: "#9ca3af", marginTop: "2px" }}>
              Ref: {item.range}
            </Typography>
          </Box>
          <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: { xs: "13px", sm: "14px" }, color: "#1f2937", fontWeight: 700 }}>
            {item.value}{item.unit}
          </Typography>
        </Box>
      ))}
    </Box>

    {/* Bottom Content */}
    <Box sx={{ marginTop: "12px", display: "flex", flexDirection: "column", gap: "8px" }}>
      {/* Limitations (Existing) */}
      <Box sx={{ padding: "10px 12px", background: "rgba(226, 232, 240, 0.5)", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px" }}>
          <Box sx={{ width: "4px", height: "4px", borderRadius: "50%", background: "#94a3b8" }} />
          <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "10px", color: "#64748b", fontWeight: 600 }}>
            Limitations
          </Typography>
        </Box>
        <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "9px", color: "#94a3b8", lineHeight: 1.5 }}>
          • No visual context for values<br />
          • Requires medical knowledge to interpret<br />
          • No actionable insights provided
        </Typography>
      </Box>

    </Box>
  </Box>
);


// ============ SMART REPORT CARD ============
const SmartReportCard = ({ testData, viewType = 'compact', profilesData = [], sx }) => {
  // Calculate counts based on view type
  let normalCount = 0;
  let abnormalCount = 0;

  if (viewType === 'total') {
    // For total view: count normal parameters from first profile's first 2 parameters
    const firstProfile = profilesData[0];
    if (firstProfile) {
      const profileParameters = testData.filter(param => firstProfile.parameterIds.includes(param.id)).slice(0, 2);
      normalCount = profileParameters.filter(item => item.value >= item.normalMin && item.value <= item.normalMax).length;
    }
  } else if (viewType === 'personal') {
    // For personal view: count normal parameters from first profile's first parameter
    const firstProfile = profilesData[0];
    if (firstProfile) {
      const firstParameter = testData.find(param => firstProfile.parameterIds.includes(param.id));
      if (firstParameter) {
        normalCount = firstParameter.value >= firstParameter.normalMin && firstParameter.value <= firstParameter.normalMax ? 1 : 0;
      }
    }
  } else {
    // For compact view: count all normal and abnormal parameters
    normalCount = testData.filter(item => item.value >= item.normalMin && item.value <= item.normalMax).length;
    abnormalCount = testData.length - normalCount;
  }

  return (
    <Box
      sx={{
        width: "100%",
        background: "linear-gradient(145deg, #ffffff 0%, #f0fdf4 100%)",
        borderRadius: "24px",
        padding: { xs: "16px", sm: "20px" },
        border: "2px solid #86efac",
        position: "relative",
        overflow: "hidden",
        animation: `${slideInRight} 0.8s ease-out 0.2s both`,
        minHeight: { xs: "285px", sm: "315px" },
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 20px 60px rgba(16, 185, 129, 0.15), 0 8px 24px rgba(53, 103, 214, 0.1)",
        ...sx,
      }}
    >
      {/* Recommended Badge */}
      <Box
        sx={{
          position: "absolute",
          top: "16px",
          right: "16px",
          background: "linear-gradient(135deg, #10b981, #059669)",
          borderRadius: "20px",
          padding: "4px 12px",
          boxShadow: "0 4px 12px rgba(16, 185, 129, 0.3)",
          animation: `${pulse} 2s ease-in-out infinite`,
        }}
      >
        <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "10px", fontWeight: 700, color: "#fff", textTransform: "uppercase", letterSpacing: "0.5px" }}>
          ✨ Smart
        </Typography>
      </Box>

      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px", marginTop: "4px" }}>
        <Box
          sx={{
            width: "40px",
            height: "40px",
            borderRadius: "12px",
            background: "linear-gradient(135deg, #3567D6, #5B8DEF)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 8px 20px rgba(53, 103, 214, 0.35)",
            animation: `${float} 3s ease-in-out infinite`,
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: "20px", color: "#fff" }} />
        </Box>
        <Box>
          <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: { xs: "16px", sm: "18px" }, fontWeight: 700, color: "#0f172a" }}>
            Smart Report
          </Typography>
          <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "11px", color: "#10b981", fontWeight: 500 }}>
            Visual insights at a glance
          </Typography>
        </Box>
      </Box>

      {/* Patient Details - Top */}
      <Box sx={{ marginBottom: "12px", padding: "10px 12px", background: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)", borderRadius: "10px", border: "1px solid #86efac" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px" }}>
          <PersonIcon sx={{ fontSize: "14px", color: "#10b981" }} />
          <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "10px", color: "#15803d", fontWeight: 600 }}>
            Patient Details
          </Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "9px", color: "#1e293b", fontWeight: 500 }}>
            Name: <Box component="span" sx={{ fontWeight: 600 }}>John Doe</Box>
          </Typography>
          <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "9px", color: "#1e293b", fontWeight: 500 }}>
            Age: <Box component="span" sx={{ fontWeight: 600 }}>35 years</Box>
          </Typography>
        </Box>
      </Box>

      {/* Status Summary */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "12px",
          gap: "12px",
        }}
      >
        {/* Heading */}
        <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "11px", fontWeight: 600, color: "#64748b" }}>
          Parameter Status
        </Typography>

        {/* Status Cards */}
        <Box
          sx={{
            display: "flex",
            gap: "8px",
            flexWrap: "wrap",
          }}
        >
          {/* Normal Card */}
          {normalCount > 0 && (
            <Box
              sx={{
                background: "linear-gradient(135deg, #dcfce7, #d1fae5)",
                borderRadius: "8px",
                padding: "6px 10px",
                display: "flex",
                alignItems: "center",
                gap: "4px",
                border: "1px solid #86efac",
                height: "28px",
              }}
            >
              <CheckCircleIcon sx={{ fontSize: "14px", color: "#15803d" }} />
              <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "10px", fontWeight: 600, color: "#15803d" }}>
                Normal {normalCount}
              </Typography>
            </Box>
          )}

          {/* Abnormal Card - Only show for compact view */}
          {viewType === 'compact' && abnormalCount > 0 && (
            <Box
              sx={{
                background: "linear-gradient(135deg, #fef3c7, #fde68a)",
                borderRadius: "8px",
                padding: "6px 10px",
                display: "flex",
                alignItems: "center",
                gap: "4px",
                border: "1px solid #fcd34d",
                height: "28px",
              }}
            >
              <WarningAmberIcon sx={{ fontSize: "14px", color: "#b45309" }} />
              <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "10px", fontWeight: 600, color: "#b45309" }}>
                Abnormal {abnormalCount}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Smart Parameters - Conditional Rendering Based on View Type */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", flex: 1 }}>
        {viewType === 'compact' && (
          // COMPACT VIEW - Existing implementation
          testData.map((item, idx) => {
            const isNormal = item.value >= item.normalMin && item.value <= item.normalMax;
            return (
              <Box
                key={idx}
                sx={{
                  background: isNormal
                    ? "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)"
                    : "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
                  border: `2px solid ${isNormal ? "#86efac" : "#fcd34d"}`,
                  borderRadius: "12px",
                  padding: { xs: "10px", sm: "12px" },
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateX(4px)",
                    boxShadow: isNormal
                      ? "0 8px 24px rgba(16, 185, 129, 0.2)"
                      : "0 8px 24px rgba(245, 158, 11, 0.2)",
                  },
                }}
              >
                {/* Name + Status Row */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "2px" }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      {item.iconPath && (
                        <Box
                          sx={{
                            width: "28px",
                            height: "28px",
                            borderRadius: "6px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "2px",
                            flexShrink: 0,
                          }}
                        >
                          <Box
                            component="img"
                            src={item.iconPath}
                            alt={item.name}
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                              display: "block",
                            }}
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        </Box>
                      )}
                      <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: { xs: "13px", sm: "14px" }, fontWeight: 700, color: "#1e293b" }}>
                        {item.name}
                      </Typography>
                      {isNormal ? (
                        <CheckCircleIcon sx={{ fontSize: "14px", color: "#10b981" }} />
                      ) : (
                        <WarningAmberIcon sx={{ fontSize: "14px", color: "#f59e0b" }} />
                      )}
                    </Box>
                    <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "10px", color: isNormal ? "#15803d" : "#b45309", fontWeight: 500 }}>
                      {item.insight}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      background: isNormal
                        ? "linear-gradient(135deg, #10b981, #059669)"
                        : "linear-gradient(135deg, #f59e0b, #d97706)",
                      borderRadius: "6px",
                      padding: "3px 8px",
                      boxShadow: isNormal
                        ? "0 4px 12px rgba(16, 185, 129, 0.3)"
                        : "0 4px 12px rgba(245, 158, 11, 0.3)",
                    }}
                  >
                    <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "9px", fontWeight: 700, color: "#fff", textTransform: "uppercase" }}>
                      {isNormal ? "Normal" : "Monitor"}
                    </Typography>
                  </Box>
                </Box>

                {/* Range Bar */}
                <CompactRangeBar
                  value={item.value}
                  min={item.min}
                  max={item.max}
                  normalMin={item.normalMin}
                  normalMax={item.normalMax}
                  unit={item.unit}
                />
              </Box>
            );
          })
        )}

        {viewType === 'total' && (
          // TOTAL VIEW - Show only first profile with first 2 parameters
          (() => {
            const firstProfile = profilesData[0];
            const profileParameters = testData.filter(param => firstProfile.parameterIds.includes(param.id)).slice(0, 2);

            // Determine profile theme based on parameters status
            const allNormal = profileParameters.every(param => param.value >= param.normalMin && param.value <= param.normalMax);
            const profileIsNormal = allNormal;

            return (
              <Box>
                {/* Profile Header - Matching parameter theme and width */}
                <Box
                  sx={{
                    background: profileIsNormal
                      ? "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)"
                      : "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
                    border: `2px solid ${profileIsNormal ? "#86efac" : "#fcd34d"}`,
                    borderRadius: "12px",
                    padding: { xs: "10px", sm: "12px" },
                    marginBottom: "10px",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Box
                      sx={{
                        width: "28px",
                        height: "28px",
                        borderRadius: "6px",
                        background: profileIsNormal
                          ? "linear-gradient(135deg, #10b981, #059669)"
                          : "linear-gradient(135deg, #f59e0b, #d97706)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: profileIsNormal
                          ? "0 4px 12px rgba(16, 185, 129, 0.3)"
                          : "0 4px 12px rgba(245, 158, 11, 0.3)",
                        flexShrink: 0,
                      }}
                    >
                      {firstProfile.icon}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: { xs: "14px", sm: "16px" }, fontWeight: 700, color: "#0f172a" }}>
                        {firstProfile.name}
                      </Typography>
                      <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "10px", color: "#64748b", marginTop: "2px" }}>
                        {firstProfile.description}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Parameters under Profile */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  {profileParameters.map((item, idx) => {
                    const isNormal = item.value >= item.normalMin && item.value <= item.normalMax;
                    return (
                      <Box
                        key={idx}
                        sx={{
                          background: isNormal
                            ? "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)"
                            : "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
                          border: `2px solid ${isNormal ? "#86efac" : "#fcd34d"}`,
                          borderRadius: "12px",
                          padding: { xs: "10px", sm: "12px" },
                        }}
                      >
                        {/* Parameter Name and Icon */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
                          {item.iconPath && (
                            <Box
                              sx={{
                                width: "28px",
                                height: "28px",
                                borderRadius: "6px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "2px",
                                flexShrink: 0,
                              }}
                            >
                              <Box
                                component="img"
                                src={item.iconPath}
                                alt={item.name}
                                sx={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "contain",
                                  display: "block",
                                }}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            </Box>
                          )}
                          <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: { xs: "13px", sm: "14px" }, fontWeight: 700, color: "#1e293b" }}>
                            {item.name}
                          </Typography>
                          {isNormal ? (
                            <CheckCircleIcon sx={{ fontSize: "14px", color: "#10b981" }} />
                          ) : (
                            <WarningAmberIcon sx={{ fontSize: "14px", color: "#f59e0b" }} />
                          )}
                        </Box>

                        {/* Parameter Description */}
                        {item.description && (
                          <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "10px", color: "#64748b", marginBottom: "13px", lineHeight: 1.4 }}>
                            {item.description}
                          </Typography>
                        )}

                        {/* Range Bar */}
                        <CompactRangeBar
                          value={item.value}
                          min={item.min}
                          max={item.max}
                          normalMin={item.normalMin}
                          normalMax={item.normalMax}
                          unit={item.unit}
                        />
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            );
          })()
        )}

        {viewType === 'personal' && (
          // PERSONAL VIEW - Show one profile with one parameter (first profile, first parameter)
          (() => {
            const firstProfile = profilesData[0];
            const firstParameter = testData.find(param => firstProfile.parameterIds.includes(param.id));
            if (!firstParameter) return null;

            const isNormal = firstParameter.value >= firstParameter.normalMin && firstParameter.value <= firstParameter.normalMax;

            return (
              <Box>
                {/* Profile Header */}
                <Box
                  sx={{
                    background: isNormal
                      ? "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)"
                      : "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
                    border: `2px solid ${isNormal ? "#86efac" : "#fcd34d"}`,
                    borderRadius: "12px",
                    padding: { xs: "12px", sm: "14px" },
                    marginBottom: "12px",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <Box
                      sx={{
                        width: "36px",
                        height: "36px",
                        borderRadius: "8px",
                        background: isNormal
                          ? "linear-gradient(135deg, #10b981, #059669)"
                          : "linear-gradient(135deg, #f59e0b, #d97706)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: isNormal
                          ? "0 4px 12px rgba(16, 185, 129, 0.3)"
                          : "0 4px 12px rgba(245, 158, 11, 0.3)",
                      }}
                    >
                      {firstProfile.icon}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: { xs: "14px", sm: "16px" }, fontWeight: 700, color: "#0f172a" }}>
                        {firstProfile.name}
                      </Typography>
                      <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "10px", color: "#64748b", marginTop: "2px" }}>
                        {firstProfile.description}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Parameter Card */}
                <Box
                  sx={{
                    background: isNormal
                      ? "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)"
                      : "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
                    border: `2px solid ${isNormal ? "#86efac" : "#fcd34d"}`,
                    borderRadius: "12px",
                    padding: { xs: "12px", sm: "14px" },
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateX(4px)",
                      boxShadow: isNormal
                        ? "0 8px 24px rgba(16, 185, 129, 0.2)"
                        : "0 8px 24px rgba(245, 158, 11, 0.2)",
                    },
                  }}
                >
                  {/* Parameter Name and Icon */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
                    {firstParameter.iconPath && (
                      <Box
                        sx={{
                          width: "28px",
                          height: "28px",
                          borderRadius: "6px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "2px",
                          flexShrink: 0,
                        }}
                      >
                        <Box
                          component="img"
                          src={firstParameter.iconPath}
                          alt={firstParameter.name}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            display: "block",
                          }}
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      </Box>
                    )}
                    <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: { xs: "14px", sm: "16px" }, fontWeight: 700, color: "#1e293b" }}>
                      {firstParameter.name}
                    </Typography>
                    {isNormal ? (
                      <CheckCircleIcon sx={{ fontSize: "16px", color: "#10b981" }} />
                    ) : (
                      <WarningAmberIcon sx={{ fontSize: "16px", color: "#f59e0b" }} />
                    )}
                  </Box>

                  {/* Parameter Description */}
                  {firstParameter.description && (
                    <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "11px", color: "#64748b", marginBottom: "8px", lineHeight: 1.5 }}>
                      {firstParameter.description}
                    </Typography>
                  )}

                  {/* Range Bar */}
                  <Box sx={{ marginBottom: "10px" }}>
                    <CompactRangeBar
                      value={firstParameter.value}
                      min={firstParameter.min}
                      max={firstParameter.max}
                      normalMin={firstParameter.normalMin}
                      normalMax={firstParameter.normalMax}
                      unit={firstParameter.unit}
                    />
                  </Box>

                  {/* Tips Section */}
                  {firstParameter.tips && firstParameter.tips.length > 0 && (
                    <Box sx={{ marginBottom: "10px", padding: "8px 10px", background: "rgba(255, 255, 255, 0.6)", borderRadius: "8px" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px" }}>
                        <LightbulbIcon sx={{ fontSize: "14px", color: "#f59e0b" }} />
                        <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "10px", fontWeight: 600, color: "#1e293b" }}>
                          Tips
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        {firstParameter.tips.map((tip, tipIdx) => (
                          <Typography key={tipIdx} sx={{ fontFamily: "'Inter', sans-serif", fontSize: "9px", color: "#475569", lineHeight: 1.4, paddingLeft: "8px", position: "relative", "&::before": { content: '"•"', position: "absolute", left: 0, color: "#f59e0b" } }}>
                            {tip}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Recommendations Section */}
                  {firstParameter.recommendations && firstParameter.recommendations.length > 0 && (
                    <Box sx={{ padding: "8px 10px", background: "rgba(255, 255, 255, 0.6)", borderRadius: "8px" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px" }}>
                        <RecommendIcon sx={{ fontSize: "14px", color: "#10b981" }} />
                        <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "10px", fontWeight: 600, color: "#1e293b" }}>
                          Recommendations
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        {firstParameter.recommendations.map((rec, recIdx) => (
                          <Typography key={recIdx} sx={{ fontFamily: "'Inter', sans-serif", fontSize: "9px", color: "#475569", lineHeight: 1.4, paddingLeft: "8px", position: "relative", "&::before": { content: `"${recIdx + 1}."`, position: "absolute", left: 0, color: "#10b981", fontWeight: 600 } }}>
                            {rec}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })()
        )}
      </Box>

    </Box>

  );
};

// ============ MAIN COMPONENT ============
const ReportComparisonSection = () => {
  const [viewType, setViewType] = useState('compact');

  // Enhanced test data with accurate medical values
  const testData = [
    {
      id: 'hemoglobin',
      name: "Hemoglobin",
      value: 14.2,
      unit: " g/dL",
      min: 8,
      max: 18,
      normalMin: 12,
      normalMax: 16,
      range: "12.0-16.0",
      insight: "Healthy oxygen capacity",
      iconPath: HemoglobinIcon,
      description: "Hemoglobin is a protein in red blood cells that carries oxygen throughout your body. Your current level of 14.2 g/dL is within the normal range, indicating healthy oxygen-carrying capacity.",
      tips: [
        "Include iron-rich foods like spinach, lentils, and lean meats in your diet",
        "Pair iron-rich foods with vitamin C sources to enhance absorption",
        "Stay hydrated to maintain optimal blood volume"
      ],
      recommendations: [
        "Continue with your current diet and lifestyle",
        "Consider regular exercise to maintain cardiovascular health",
        "Schedule a follow-up test in 6 months to monitor levels"
      ]
    },
    {
      id: 'wbc',
      name: "WBC Count",
      value: 6.8,
      unit: " ×10³/μL",
      min: 2.0,
      max: 15.0,
      normalMin: 4.0,
      normalMax: 11.0,
      range: "4.0-11.0",
      insight: "Normal immune response",
      iconPath: WBCIcon,
      description: "White Blood Cell (WBC) count measures the number of immune cells in your blood. Your level of 6.8 ×10³/μL is within the normal range, indicating a healthy immune system.",
      tips: [
        "Maintain good hygiene practices to support immune health",
        "Get adequate sleep (7-9 hours) to support immune function",
        "Stay active with regular moderate exercise"
      ],
      recommendations: [
        "Your immune system appears healthy - continue current practices",
        "Consider seasonal flu vaccinations as recommended",
        "Monitor for any signs of infection and consult a doctor if needed"
      ]
    },
    {
      id: 'glucose',
      name: "Glucose",
      value: 110,
      unit: " mg/dL",
      min: 50,
      max: 150,
      normalMin: 70,
      normalMax: 100,
      range: "70-100",
      insight: "Slightly elevated - watch diet",
      iconPath: GlucoseIcon,
      description: "Blood glucose measures the amount of sugar in your blood. Your level is slightly above the normal range, which may indicate the need for dietary adjustments.",
      tips: [
        "Reduce intake of refined sugars and processed foods",
        "Choose whole grains over white bread and pasta",
        "Eat smaller, more frequent meals to stabilize blood sugar"
      ],
      recommendations: [
        "Consult with a healthcare provider for personalized dietary guidance",
        "Consider monitoring blood sugar levels regularly",
        "Incorporate regular physical activity into your routine"
      ]
    },
  ];

  // Profile data for Total view - Only Complete Blood Count profile
  const profilesData = [
    {
      id: 'cbc',
      name: "Complete Blood Count (CBC)",
      description: "Comprehensive analysis of blood cells including red blood cells (RBC), white blood cells (WBC), and platelets to assess overall blood health",
      icon: <BloodtypeIcon sx={{ fontSize: "18px", color: "#fff" }} />,
      parameterIds: ['hemoglobin', 'wbc']
    }
  ];

  return (
    <Box
      sx={{
        width: "100%",
        padding: { xs: "48px 16px", sm: "60px 24px", md: "80px 40px" },
        backgroundColor: "transparent",
        overflow: "hidden",
      }}
    >
      {/* Section Header */}
      <Box
        sx={{
          textAlign: "center",
          marginBottom: { xs: "36px", md: "56px" },
          animation: `${fadeInUp} 0.6s ease-out`,
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            background: "linear-gradient(135deg, rgba(53,103,214,0.1), rgba(16,185,129,0.1))",
            borderRadius: "50px",
            padding: "8px 20px",
            marginBottom: "20px",
            border: "1px solid rgba(53,103,214,0.2)",
          }}
        >
          <Typography sx={{ fontSize: "16px" }}>🔄</Typography>
          <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "13px", fontWeight: 600, color: "#3567D6" }}>
            Before & After
          </Typography>
        </Box>

        <Typography
          sx={{
            fontFamily: "'Poppins', sans-serif",
            fontSize: { xs: "28px", sm: "36px", md: "46px" },
            fontWeight: 800,
            color: "#0f172a",
            lineHeight: 1.15,
            letterSpacing: "-1px",
            marginBottom: "16px",
          }}
        >
          Reports Patients{" "}
          <Box
            component="span"
            sx={{
              background: "linear-gradient(135deg, #3567D6 0%, #10b981 50%, #8b5cf6 100%)",
              backgroundSize: "200% 100%",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              animation: `${shimmer} 5s linear infinite`,
            }}
          >
            Actually Love (90 NPS)
          </Box>
        </Typography>
        <Typography
          sx={{
            fontFamily: "'Inter', sans-serif",
            fontSize: { xs: "14px", sm: "16px", md: "18px" },
            color: "#64748b",
            maxWidth: "700px",
            margin: "0 auto",
            lineHeight: 1.6,
          }}
        >
          Color-coded clarity, instant abnormal flags, and personalized health tips compare how our smart reports make your health data simple, actionable, and stress-free.</Typography>
      </Box>

      {/* Button Toggle Group */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: { xs: "8px", sm: "12px" },
          marginBottom: { xs: "24px", sm: "32px" },
          maxWidth: "1200px",
          marginLeft: "auto",
          marginRight: "auto",
          animation: `${fadeInUp} 0.6s ease-out 0.2s both`,
        }}
      >
        {['personal', 'total', 'compact',].map((type) => (
          <Button
            key={type}
            onClick={() => setViewType(type)}
            sx={{
              textTransform: "capitalize",
              fontFamily: "'Inter', sans-serif",
              fontSize: { xs: "12px", sm: "14px" },
              fontWeight: 600,
              padding: { xs: "8px 16px", sm: "10px 24px" },
              borderRadius: "12px",
              border: viewType === type ? "2px solid transparent" : "2px solid #e2e8f0",
              background: viewType === type
                ? "linear-gradient(135deg, #3567D6 0%, #10b981 100%)"
                : "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
              color: viewType === type ? "#fff" : "#64748b",
              boxShadow: viewType === type
                ? "0 4px 12px rgba(53, 103, 214, 0.3)"
                : "0 2px 8px rgba(0, 0, 0, 0.05)",
              transition: "all 0.3s ease",
              "&:hover": {
                background: viewType === type
                  ? "linear-gradient(135deg, #3567D6 0%, #10b981 100%)"
                  : "linear-gradient(145deg, #f1f5f9 0%, #e2e8f0 100%)",
                transform: "translateY(-2px)",
                boxShadow: viewType === type
                  ? "0 6px 16px rgba(53, 103, 214, 0.4)"
                  : "0 4px 12px rgba(0, 0, 0, 0.1)",
              },
            }}
          >
            {type.charAt(0).toUpperCase() + type.slice(1)}
          </Button>
        ))}
      </Box>

      {/* Cards Container */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
          gap: { xs: "24px", sm: "32px", md: "40px" },
          maxWidth: "1200px",
          margin: "0 auto",
          alignItems: "stretch",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", height: "100%" }}>
          <TraditionalReportCard testData={testData} sx={{ flex: 1 }} />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              padding: "16px",
              background: "#fff",
              borderRadius: "16px",
              border: "1px solid #e2e8f0",
              boxShadow: "0 4px 12px rgba(0,0,0,0.03)"
            }}
          >
            <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "12px", fontWeight: 600, color: "#64748b", marginBottom: "4px" }}>
              The Challenges
            </Typography>
            {[
              "Takes 15+ mins to decode",
              "Requires doctor consultation to understand"
            ].map((text, i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <Box sx={{ minWidth: "20px", height: "20px", display: "flex", alignItems: "center", justifyContent: "center", background: "#fef2f2", borderRadius: "50%" }}>
                  <CancelIcon sx={{ fontSize: "14px", color: "#ef4444" }} />
                </Box>
                <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "13px", color: "#64748b", fontWeight: 500 }}>
                  {text}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", height: "100%" }}>
          <SmartReportCard testData={testData} viewType={viewType} profilesData={profilesData} sx={{ flex: 1 }} />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              padding: "16px",
              background: "linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)",
              borderRadius: "16px",
              border: "1px solid #86efac",
              boxShadow: "0 8px 24px rgba(16, 185, 129, 0.1)"
            }}
          >
            <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "12px", fontWeight: 600, color: "#10b981", marginBottom: "4px" }}>
              The Solution
            </Typography>
            {[
              "Understand in under 2 minutes",
              "Visual insights at a glance",
              "Know what to do next—instantly"
            ].map((text, i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <Box sx={{ minWidth: "20px", height: "20px", display: "flex", alignItems: "center", justifyContent: "center", background: "#ecfdf5", borderRadius: "50%" }}>
                  <CheckCircleIcon sx={{ fontSize: "14px", color: "#10b981" }} />
                </Box>
                <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "13px", color: "#0f172a", fontWeight: 600 }}>
                  {text}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Feature Pills */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(4, minmax(0, 1fr))"
          },
          gap: { xs: "10px", sm: "12px", md: "12px" },
          justifyContent: "center",
          marginTop: { xs: "32px", sm: "36px", md: "48px" },
          padding: { xs: "0 8px", sm: "0 16px", md: "0" },
          maxWidth: "1200px",
          marginLeft: "auto",
          marginRight: "auto",
          animation: `${fadeInUp} 0.6s ease-out 0.4s both`,
        }}
      >
        {[
          { icon: "📊", label: "Visual Range Bars", color: "#3567D6", gradient: "linear-gradient(135deg, rgba(53,103,214,0.05), rgba(53,103,214,0.02))" },
          { icon: "🎯", label: "Clear Status", color: "#10b981", gradient: "linear-gradient(135deg, rgba(16,185,129,0.05), rgba(16,185,129,0.02))" },
          { icon: "💡", label: "Plain Language", color: "#8b5cf6", gradient: "linear-gradient(135deg, rgba(139,92,246,0.05), rgba(139,92,246,0.02))" },
          { icon: "⚡", label: "Instant Understanding", color: "#f59e0b", gradient: "linear-gradient(135deg, rgba(245,158,11,0.05), rgba(245,158,11,0.02))" },
        ].map((item, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: { xs: "center", sm: "flex-start" },
              gap: { xs: "8px", sm: "10px", md: "10px" },
              background: item.gradient,
              borderRadius: "50px",
              padding: { xs: "10px 16px", sm: "12px 20px", md: "14px 24px" },
              border: `1.5px solid ${item.color}20`,
              boxShadow: `0 4px 20px ${item.color}10, 0 2px 8px rgba(0,0,0,0.03)`,
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              cursor: "default",
              position: "relative",
              overflow: "hidden",
              width: "100%",
              minWidth: 0,
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: "-100%",
                width: "100%",
                height: "100%",
                background: `linear-gradient(90deg, transparent, ${item.color}15, transparent)`,
                transition: "left 0.5s ease",
              },
              "&:hover": {
                transform: { xs: "translateY(-2px)", sm: "translateY(-4px)", md: "translateY(-6px) scale(1.03)" },
                boxShadow: `0 12px 32px ${item.color}20, 0 4px 16px rgba(0,0,0,0.06)`,
                borderColor: `${item.color}40`,
                background: `linear-gradient(135deg, ${item.color}10, ${item.color}05)`,
                "&::before": {
                  left: "100%",
                },
              },
            }}
          >
            <Box
              sx={{
                width: { xs: "24px", sm: "28px", md: "32px" },
                height: { xs: "24px", sm: "28px", md: "32px" },
                borderRadius: "50%",
                background: `linear-gradient(135deg, ${item.color}20, ${item.color}15)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: `0 2px 8px ${item.color}20`,
                flexShrink: 0,
                border: `1px solid ${item.color}30`,
              }}
            >
              <Typography sx={{ fontSize: { xs: "12px", sm: "14px", md: "18px" } }}>{item.icon}</Typography>
            </Box>
            <Typography
              sx={{
                fontFamily: "'Inter', sans-serif",
                fontSize: { xs: "11px", sm: "13px", md: "15px" },
                fontWeight: 600,
                color: "#1e293b",
                letterSpacing: "-0.2px",
                whiteSpace: { xs: "nowrap", md: "normal" },
                overflow: { xs: "hidden", md: "visible" },
                textOverflow: { xs: "ellipsis", md: "clip" },
                flex: 1,
                minWidth: 0,
              }}
            >
              {item.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default ReportComparisonSection;
